import { useCallback, useState } from 'react'
import { useDropzone, type FileRejection } from 'react-dropzone'
import { cn } from '@/lib/utils'
import { Button } from './Button'

export interface UploadedFile {
  file: File
  id: string
  status: 'pending' | 'uploading' | 'success' | 'error'
  progress?: number
  error?: string
}

export interface FileUploaderProps {
  onFilesSelected?: (files: File[]) => void
  onUpload?: (files: File[]) => Promise<void>
  autoUpload?: boolean
  accept?: Record<string, string[]>
  maxFiles?: number
  maxSize?: number
  disabled?: boolean
  className?: string
}

const ACCEPTED_TYPES = {
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'text/markdown': ['.md'],
  'text/plain': ['.txt'],
  'application/json': ['.json'],
  'text/csv': ['.csv'],
}

const FileUploader = ({
  onFilesSelected,
  onUpload,
  autoUpload = false,
  accept = ACCEPTED_TYPES,
  maxFiles = 10,
  maxSize = 10 * 1024 * 1024, // 10MB
  disabled = false,
  className,
}: FileUploaderProps) => {
  const [files, setFiles] = useState<UploadedFile[]>([])
  const [isUploading, setIsUploading] = useState(false)

  const startUpload = async (targetFiles: UploadedFile[]) => {
    if (!onUpload || targetFiles.length === 0) return

    const targetIds = new Set(targetFiles.map((f) => f.id))

    setIsUploading(true)
    setFiles((prev) =>
      prev.map((f) =>
        targetIds.has(f.id) ? { ...f, status: 'uploading' as const } : f
      )
    )

    try {
      await onUpload(targetFiles.map((f) => f.file))
      setFiles((prev) =>
        prev.map((f) =>
          targetIds.has(f.id) ? { ...f, status: 'success' as const } : f
        )
      )
    } catch (error) {
      setFiles((prev) =>
        prev.map((f) =>
          targetIds.has(f.id)
            ? { ...f, status: 'error' as const, error: 'Echec de l\'envoi' }
            : f
        )
      )
    } finally {
      setIsUploading(false)
    }
  }

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      const shouldAutoUpload = autoUpload && !!onUpload && acceptedFiles.length > 0
      const newFiles: UploadedFile[] = acceptedFiles.map((file) => ({
        file,
        id: `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        status: shouldAutoUpload ? 'uploading' : 'pending',
      }))

      // Handle rejected files
      const rejectedWithErrors: UploadedFile[] = rejectedFiles.map(({ file, errors }) => ({
        file,
        id: `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        status: 'error' as const,
        error: errors.map((e) => e.message).join(', '),
      }))

      setFiles((prev) => [...prev, ...newFiles, ...rejectedWithErrors])
      onFilesSelected?.(acceptedFiles)

      if (shouldAutoUpload) {
        void startUpload(newFiles)
      }
    },
    [autoUpload, onFilesSelected, onUpload]
  )

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
    onDrop,
    accept,
    maxFiles,
    maxSize,
    disabled: disabled || isUploading,
  })

  const removeFile = (id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id))
  }

  const clearAll = () => {
    setFiles([])
  }

  const handleUpload = async () => {
    if (!onUpload || files.length === 0) return

    const pendingFiles = files.filter((f) => f.status === 'pending')
    if (pendingFiles.length === 0) return
    await startUpload(pendingFiles)
  }

  const pendingCount = files.filter((f) => f.status === 'pending').length
  const successCount = files.filter((f) => f.status === 'success').length
  const errorCount = files.filter((f) => f.status === 'error').length

  const getDropzoneStyles = () => {
    if (isDragReject) {
      return 'border-red-500 bg-red-500/5'
    }
    if (isDragAccept) {
      return 'border-primary bg-primary/10'
    }
    if (isDragActive) {
      return 'border-primary/50 bg-primary/5'
    }
    return 'border-border hover:border-border-hover'
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`
  }

  const getStatusIcon = (status: UploadedFile['status']) => {
    switch (status) {
      case 'pending':
        return <span className="text-foreground-muted">●</span>
      case 'uploading':
        return <span className="text-primary animate-pulse">◐</span>
      case 'success':
        return <span className="text-primary">✓</span>
      case 'error':
        return <span className="text-red-500">✗</span>
    }
  }

  const getFileExtension = (filename: string) => {
    return filename.split('.').pop()?.toUpperCase() || 'FILE'
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* Zone de drop principale */}
      <div
        {...getRootProps()}
        className={cn(
          'relative border-2 border-dashed p-8 lg:p-12 transition-all duration-200 cursor-pointer',
          'bg-surface',
          getDropzoneStyles(),
          (disabled || isUploading) && 'opacity-50 cursor-not-allowed'
        )}
      >
        <input {...getInputProps()} />

        <div className="flex flex-col items-center justify-center text-center space-y-4">
          {/* Icone centrale */}
          <div
            className={cn(
              'w-20 h-20 border-2 flex items-center justify-center transition-colors duration-200',
              isDragAccept
                ? 'border-primary text-primary'
                : isDragReject
                  ? 'border-red-500 text-red-500'
                  : 'border-border text-foreground-muted'
            )}
          >
            <svg
              className="w-10 h-10"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="square"
                strokeLinejoin="miter"
                strokeWidth={1.5}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
          </div>

          {/* Texte principal */}
          <div className="space-y-2">
            {isDragReject ? (
              <p className="text-lg font-semibold text-red-500">
                Format non accepté
              </p>
            ) : isDragAccept ? (
              <p className="text-lg font-semibold text-primary">
                Déposez pour ajouter
              </p>
            ) : (
              <>
                <p className="text-lg font-semibold text-foreground">
                  Glissez vos documents ici
                </p>
                <p className="text-sm text-foreground-secondary">
                  ou cliquez pour sélectionner
                </p>
              </>
            )}
          </div>

          {/* Formats acceptés */}
          <div className="flex flex-wrap justify-center gap-2 mt-4">
            {Object.values(accept)
              .flat()
              .map((ext) => (
                <span
                  key={ext}
                  className="px-3 py-1 text-xs font-mono uppercase border border-border bg-bg text-foreground-muted"
                >
                  {ext}
                </span>
              ))}
          </div>

          {/* Limite */}
          <p className="text-xs font-mono text-foreground-muted">
            Maximum {maxFiles} fichiers · {formatFileSize(maxSize)} par fichier
          </p>
        </div>
      </div>

      {/* Liste des fichiers */}
      {files.length > 0 && (
        <div className="border border-border bg-surface">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border bg-bg">
            <div className="flex items-center gap-4">
              <span className="text-sm font-mono text-foreground">
                {files.length} fichier{files.length > 1 ? 's' : ''}
              </span>
              {pendingCount > 0 && (
                <span className="text-xs font-mono text-foreground-muted">
                  {pendingCount} en attente
                </span>
              )}
              {successCount > 0 && (
                <span className="text-xs font-mono text-primary">
                  {successCount} envoyé{successCount > 1 ? 's' : ''}
                </span>
              )}
              {errorCount > 0 && (
                <span className="text-xs font-mono text-red-500">
                  {errorCount} erreur{errorCount > 1 ? 's' : ''}
                </span>
              )}
            </div>
            <button
              onClick={clearAll}
              className="text-xs font-mono text-foreground-muted hover:text-red-500 transition-colors"
            >
              Tout effacer
            </button>
          </div>

          {/* File list */}
          <div className="divide-y divide-border max-h-64 overflow-y-auto">
            {files.map((uploadedFile) => (
              <div
                key={uploadedFile.id}
                className={cn(
                  'flex items-center justify-between p-4 transition-colors',
                  uploadedFile.status === 'error' && 'bg-red-500/5'
                )}
              >
                <div className="flex items-center gap-4 min-w-0">
                  {/* Status */}
                  <div className="text-lg">{getStatusIcon(uploadedFile.status)}</div>

                  {/* File type badge */}
                  <span className="px-2 py-1 text-xs font-mono uppercase border border-border bg-bg text-foreground-muted shrink-0">
                    {getFileExtension(uploadedFile.file.name)}
                  </span>

                  {/* File info */}
                  <div className="min-w-0">
                    <p className="text-sm font-mono text-foreground truncate">
                      {uploadedFile.file.name}
                    </p>
                    {uploadedFile.error ? (
                      <p className="text-xs text-red-500">{uploadedFile.error}</p>
                    ) : (
                      <p className="text-xs text-foreground-muted">
                        {formatFileSize(uploadedFile.file.size)}
                      </p>
                    )}
                  </div>
                </div>

                {/* Remove button */}
                {uploadedFile.status !== 'uploading' && (
                  <button
                    onClick={() => removeFile(uploadedFile.id)}
                    className="p-2 text-foreground-muted hover:text-red-500 transition-colors"
                    aria-label="Supprimer"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="square" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Actions */}
          {pendingCount > 0 && onUpload && !autoUpload && (
            <div className="p-4 border-t border-border bg-bg">
              <Button
                variant="primary"
                size="lg"
                onClick={handleUpload}
                disabled={isUploading}
                className="w-full"
              >
                {isUploading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-pulse">◐</span>
                    Envoi en cours...
                  </span>
                ) : (
                  `Envoyer ${pendingCount} fichier${pendingCount > 1 ? 's' : ''}`
                )}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

FileUploader.displayName = 'FileUploader'

export { FileUploader }
